import { createPortal } from 'react-dom';

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  tone = 'danger',
  confirming = false,
  confirmingLabel = 'Please wait...',
}) {
  if (!open || typeof document === 'undefined') {
    return null;
  }

  const toneStyles = {
    danger: {
      button: 'bg-red-600 hover:bg-red-700 text-white',
      icon: 'bg-red-50 text-red-600 ring-red-100',
    },
    success: {
      button: 'bg-emerald-600 hover:bg-emerald-700 text-white',
      icon: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
    },
    default: {
      button: 'crm-btn-primary',
      icon: 'bg-blue-50 text-blue-600 ring-blue-100',
    },
  };
  const styles = toneStyles[tone] || toneStyles.default;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] grid place-items-center bg-slate-950/55 p-4 backdrop-blur-[2px]"
      role="presentation"
      onClick={confirming ? undefined : onCancel}
    >
      <div
        className="crm-panel w-full max-w-md overflow-hidden shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-4 px-6 pb-5 pt-6">
          <span
            className={`grid h-11 w-11 shrink-0 place-items-center rounded-full ring-4 ${styles.icon}`}
            aria-hidden="true"
          >
            {tone === 'danger' ? (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 9V13" strokeLinecap="round" />
                <path d="M12 17H12.01" strokeLinecap="round" />
                <path d="M10.3 4.5L3.2 17A2 2 0 0 0 5 20H19A2 2 0 0 0 20.8 17L13.7 4.5A2 2 0 0 0 10.3 4.5Z" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 12L10.5 15.5L17 8.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="12" r="9" />
              </svg>
            )}
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="confirm-dialog-title" className="text-lg font-semibold">
              {title}
            </h2>
            {message ? (
              <p className="crm-text-muted mt-2 text-sm leading-relaxed">{message}</p>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-2 border-t border-[rgb(var(--crm-border))] bg-[rgb(var(--crm-page-bg))] px-6 py-4">
          <button
            type="button"
            className="crm-btn-secondary"
            onClick={onCancel}
            disabled={confirming}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`inline-flex min-h-10 items-center justify-center rounded-md px-4 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${styles.button}`}
            onClick={onConfirm}
            disabled={confirming}
          >
            {confirming ? confirmingLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ConfirmDialog;
